class Cart extends HTMLElement {
  constructor() {
    super();
    this.upsellRequest = null;
    this.handleToggleDrawerCart();
    this.handleQuantityFields();
    this.handleAddToCart();
    this.handleRestoredPage();
    this.syncDiscountNotice();
  }

  // A page restored from the browser's back/forward cache comes back with the DOM it was
  // unloaded with, so a cart changed in the meantime — at checkout, or in another tab —
  // leaves this page describing the old one. This is the path that produced the reported
  // desync: check out, change the cart there, navigate back.
  //
  // Reconciled on restore rather than waiting for the next drawer open, because the header
  // badge is on screen without the drawer being opened at all. A page served stale from the
  // CDN cache is a normal load and never fires this, which is why the open handler syncs too.
  handleRestoredPage() {
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.syncCartFromServer();
      // The discount can have gone away in the same round trip — removing it at checkout
      // clears the cookie this reads — so re-test rather than leaving the notice asserting
      // a discount that is no longer on the cart.
      this.syncDiscountNotice();
    });
  }
   
  // A discount can be live while the cart is still empty, and Liquid cannot tell us: manual
  // discount codes are not exposed through cart.discount_applications, and an empty cart has
  // no allocations to read anyway. Shopify sets a discount_code cookie when /discount/<code>
  // is hit — which is exactly what the email-capture popup does in the background — so the
  // cookie is the signal. assets/email-capture.js reads the same one to suppress itself.
  //
  // Taking the code from the cookie rather than a theme setting means this covers any
  // discount link the visitor arrived on, not just the one the popup hands out.
  // Re-read the cart from the server and swap the authoritative markup in.
  //
  // The drawer's line items, subtotal, item count and empty/not-empty class are all rendered
  // by Liquid when the page is built, and nothing reconciled them afterwards. That HTML goes
  // stale the moment the cart changes anywhere else — most obviously on a page restored from
  // the browser's back/forward cache after a checkout round trip, where it describes the cart
  // as it was. handleAddToCart then patched that stale DOM (append a line item it could not
  // find) while the counters were set from a fresh /cart.js, so the count ended up right and
  // the list wrong: an empty-looking drawer over a cart with contents, or a count higher than
  // the number of items rendered.
  //
  // Going through the Section Rendering API rather than /cart.js means the line items come
  // back rendered by the same Liquid that builds them on a full page load, so there is no
  // second copy of that markup to keep in step.
  //
  // Only the cart-dependent parts are replaced, deliberately not the whole container: the
  // close buttons live inside .js-cart-content and carry per-element listeners that an
  // innerHTML swap would silently drop.
  syncCartFromServer() {
    const root = window.Shopify?.routes?.root || '/';
    // Opens can outpace responses; only the newest one is allowed to paint.
    const token = (this.syncToken || 0) + 1;
    this.syncToken = token;

    return fetch(`${root}?sections=drawer-cart`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data) => {
        if (this.syncToken !== token) return;
        this.applyCartMarkup(data['drawer-cart']);
      })
      .catch(() => {
        // Leave what is on screen. The drawer still works and the next open retries — but
        // never leave the pending state up, or the items list stays a skeleton for good.
        document.querySelector('.js-cart-content')?.classList.remove('drawer-cart__content--adding');
      });
  }

  // The painting half of the above, separated so the add-to-cart path can hand over markup
  // that came back bundled with the add itself (see handleAddToCart) instead of paying for a
  // second request to fetch the same thing.
  applyCartMarkup(markup) {
    if (!markup) return;

    // Bundled markup is newer than any in-flight fetch, so it claims the token too.
    this.syncToken = (this.syncToken || 0) + 1;

    const fresh = new DOMParser()
      .parseFromString(markup, 'text/html')
      .querySelector('.js-cart-content');
    const content = document.querySelector('.js-cart-content');
    if (!fresh || !content) return;

    const items = content.querySelector('.js-cart-items');
    const freshItems = fresh.querySelector('.js-cart-items');
    if (items && freshItems) items.innerHTML = freshItems.innerHTML;

    // The free shipping row is replaced whole — copy and bar together.
    const shipping = content.querySelector('.js-cart-free-shipping');
    const freshShipping = fresh.querySelector('.js-cart-free-shipping');
    if (shipping && freshShipping) {
      // The bar arrives already at its new value. Start it at the old one and let it run to
      // the new on the next frame, so an add reads as the bar filling — and the truck driving
      // along it — rather than a jump. Fill and marker both follow the one custom property.
      const imported = document.importNode(freshShipping, true);
      const bar = imported.querySelector('.js-cart-free-shipping-bar');
      const from = parseInt(shipping.dataset.progress, 10) || 0;
      const to = parseInt(imported.dataset.progress, 10) || 0;
      if (bar && from !== to) {
        bar.style.setProperty('--free-shipping-progress', `${from}%`);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          bar.style.setProperty('--free-shipping-progress', `${to}%`);
        }));
      }
      // Only the change that crosses the threshold celebrates; the CSS holds the pop and the
      // burst back until the bar has finished its run to the end.
      if (shipping.dataset.unlocked !== 'true' && imported.dataset.unlocked === 'true') {
        imported.classList.add('cart-free-shipping--celebrate');
      }
      shipping.replaceWith(imported);
    }

    const taxesInfo = content.querySelector('.js-cart-taxes-info');
    const freshTaxesInfo = fresh.querySelector('.js-cart-taxes-info');
    if (taxesInfo && freshTaxesInfo) taxesInfo.innerHTML = freshTaxesInfo.innerHTML;

    const subtotal = content.querySelector('.js-subtotal-price');
    const freshSubtotal = fresh.querySelector('.js-subtotal-price');
    if (subtotal && freshSubtotal) subtotal.innerHTML = freshSubtotal.innerHTML;

    const freshCounter = fresh.querySelector('.js-cart-counter');
    if (freshCounter) {
      const count = freshCounter.textContent.trim();
      document.querySelectorAll('.js-cart-counter').forEach((el) => {
        el.innerHTML = count;
        el.setAttribute('data-counter', count);
      });
    }

    // The single thing most likely to be wrong on a restored page.
    content.classList.toggle(
      'drawer-cart__content--empty',
      fresh.classList.contains('drawer-cart__content--empty')
    );

    // Replaced line items bring their own lazy images with them.
    if (typeof window.initializeLazyObservers === 'function') window.initializeLazyObservers();

    // Whatever was pending has now landed.
    content.classList.remove('drawer-cart__content--adding');

    // For anything else on the page drawn from the same response — the collection pages'
    // free shipping bar (assets/cart-bundle-bar.js) takes its copy from a <template> in here.
    document.dispatchEvent(new CustomEvent('cart:markup', { detail: { fresh } }));
  }

  // These two mirror the price markup in sections/drawer-cart.liquid so a cart mutated
  // client-side renders the same as one rendered by Liquid. /cart.js, /cart/add.js and
  // /cart/update.js all return the original_* figures alongside the final ones, so the same
  // "did a discount move the price" test works here.
  //
  // Shopify.formatMoney with moneyWithCurrencyFormat is what every other price in this file
  // uses; kept the same so the struck-through price and the live one are formatted alike.
  subtotalMarkup(cart) {
    const now = Shopify.formatMoney(cart.total_price, moneyWithCurrencyFormat);
    if (!cart.original_total_price || cart.original_total_price <= cart.total_price) return now;

    const was = Shopify.formatMoney(cart.original_total_price, moneyWithCurrencyFormat);
    return `<s class="subtotal__price-was">${was}</s> <span class="subtotal__price-now">${now}</span>`;
  }

  linePriceMarkup(item) {
    const now = Shopify.formatMoney(item.final_price, moneyWithCurrencyFormat);
    if (!item.original_price || item.original_price <= item.final_price) return now;

    const was = Shopify.formatMoney(item.original_price, moneyWithCurrencyFormat);
    return `<s class="cart-item__price-was">${was}</s> <span class="cart-item__price-now">${now}</span>`;
  }

  activeDiscountCode() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('discount');
      if (fromUrl) return fromUrl.trim();
    } catch (e) {
      /* ignore */
    }

    const match = /(?:^|;\s*)discount_code=([^;]+)/.exec(document.cookie || '');
    if (!match) return '';

    try {
      return decodeURIComponent(match[1]).trim();
    } catch (e) {
      return match[1].trim();
    }
  }

  syncDiscountNotice() {
    const notice = document.querySelector('.js-drawer-cart-discount');
    if (!notice) return;

    const code = this.activeDiscountCode();
    if (!code) {
      notice.hidden = true;
      return;
    }

    const codeEl = notice.querySelector('.js-drawer-cart-discount-code');
    if (codeEl) codeEl.textContent = code;
    notice.hidden = false;
  }

  // The upsell markup lives in sections/cart-upsell.liquid and is pulled in the first time the
  // drawer opens. Rendering it inline meant ~100KB of hidden markup on every page of the site,
  // for eight products most visitors never scrolled to. Routed through Shopify.routes.root so
  // the market/locale context is preserved and prices come back in the presentment currency.
  ensureCartUpsell() {
    if (this.upsellRequest) return this.upsellRequest;

    const slots = document.querySelectorAll('.js-cart-upsell-slot');
    if (!slots.length) return null;

    const root = window.Shopify?.routes?.root || '/';

    this.upsellRequest = fetch(`${root}?sections=cart-upsell`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data) => {
        const markup = data['cart-upsell'];
        if (!markup) return;

        const parsed = new DOMParser().parseFromString(markup, 'text/html');

        slots.forEach((slot) => {
          const source = parsed.querySelector(`[data-upsell-slot="${slot.dataset.upsellSlot}"]`);
          if (source) slot.innerHTML = source.innerHTML;
        });

        // Clicks are delegated on document and keep working, but the variant lookup and
        // intersection observers are bound per element, so the injected cards need wiring.
        if (typeof window.observeGridItems === 'function') window.observeGridItems();
        // And their images need the lazyload observer: .fade-in is opacity 0 until it adds
        // .show, and assets/lazyload.js only runs itself on DOMContentLoaded — which is long
        // gone by the time this markup arrives. Without this the cards render blank.
        if (typeof window.initializeLazyObservers === 'function') window.initializeLazyObservers();
      })
      .catch(() => {
        // The drawer is perfectly usable without the upsell — clear the guard so the next
        // open can try again rather than leaving the slots permanently empty.
        this.upsellRequest = null;
      });

    return this.upsellRequest;
  }

  // Pulled out of the open button's handler so add-to-cart can open the drawer the same way,
  // at click time, instead of waiting on the network first.
  openDrawer() {
    const drawer = document.querySelector('.js-drawer-cart');
    if (!drawer) return;

    drawer.classList.add('display');
    if (window.lenis) {
      window.lenis.stop();
    } else {
      document.body.style.overflow = "hidden";
    }

    setTimeout(() => {
      drawer.classList.add('active');
    }, 50);
  }

  handleToggleDrawerCart() {
    document.querySelectorAll('.js-open-drawer-cart').forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        this.openDrawer();
        this.ensureCartUpsell();
        // This page's cart markup may predate the cart's current contents.
        this.syncCartFromServer();
        // The popup may have applied a discount since this page was rendered.
        this.syncDiscountNotice();
      });
    });

    document.querySelectorAll('.js-close-drawer-cart').forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const drawer = document.querySelector('.js-drawer-cart');
        if (!drawer) return;

        drawer.classList.add('closing');

        setTimeout(() => {
          drawer.classList.remove('active', 'closing', 'display');
          if (window.lenis) {
            window.lenis.start();
          } else {
            document.body.style.overflow = "visible";
          }
        }, 300);
      });
    });
  }

  handleQuantityFields() {
    const self = this;
    document.body.addEventListener('click', (event) => {
			const target = event.target.closest('.js-change-quantity');
			if (!target) return;

      event.preventDefault();
  
      target.classList.add('loading');
			  
			const variantId = parseInt(target.getAttribute('data-variant-id'), 10);
			let quantity = parseInt(target.getAttribute('data-quantity'), 10);
      const container = target.closest('.js-quantity-fields');

      container.querySelector('.js-quantity-minus').setAttribute('data-quantity', quantity - 1);
      container.querySelector('.js-quantity-plus').setAttribute('data-quantity', quantity + 1);

      const cartItem = container.closest('.js-cart-item');
      cartItem.style.height = `${cartItem.offsetHeight}px`;
			  
      var formData = new FormData();
      formData.append("updates[" + variantId + "]", quantity);

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(response => {
        document.querySelectorAll('.js-change-quantity').forEach(el => el.classList.remove('loading'));
				const subtotalElement = document.querySelector('.js-subtotal-price');
				if (subtotalElement) {
					subtotalElement.innerHTML = self.subtotalMarkup(response);
				}
        
        if (quantity < 1) {
          cartItem.classList.add('hide-item');
          setTimeout(() => {
            cartItem.classList.add('hidden');
            if (response.item_count === 0) {
              document.querySelector('.js-cart-content').classList.add('drawer-cart__content--empty');
            }
          }, 300);
        } else {
          container.querySelector('.js-quantity-field-input').value = quantity;
        }

        cartItem.setAttribute('data-quantity', quantity);
        
				document.querySelectorAll('.js-cart-counter').forEach(cartCounter => {
				  cartCounter.innerHTML = response.item_count;
				  cartCounter.setAttribute('data-counter', response.item_count);
				});

        // The updates above are optimistic — enough to feel instant. This makes them true.
        self.syncCartFromServer();
      })
      .catch(error => {
        alert(error.message || 'Error');
        document.querySelectorAll('.js-change-quantity').forEach(el => el.classList.remove('loading'));
      });
    });
  }

  handleAddToCart() {
    const self = this;

		document.body.addEventListener("click", function (event) {
      const target = event.target.closest(".js-add-to-cart");
	    if (!target) return;

	    event.preventDefault();

      target.getAttribute("data-adding-text") && (target.innerHTML = target.getAttribute("data-adding-text"));
      target.classList.add("loading");

      if (target.closest(".js-mobile-add-to-cart")) {
        return;
      }

      // Open now, not when the network comes back. /cart/add.js is a cart write — the slowest
      // class of storefront request — and the drawer used to wait on it and on a second
      // /cart.js call before anything appeared, which is why this felt like a dead click for
      // a second or more. The contents reconcile from the response a moment later.
      self.openDrawer();
      // Kick the upsell off alongside the add rather than after it, so the heaviest payload
      // in the drawer is not fetched during the open animation.
      self.ensureCartUpsell();
      // Pending state for the items list, so an add from an empty cart does not flash
      // "your bag is empty" at someone who just added something.
      const content = document.querySelector('.js-cart-content');
      if (content) {
        content.classList.remove('drawer-cart__content--empty');
        content.classList.add('drawer-cart__content--adding');
      }

      let id = null;
      let quantity = 1;

      const gridItem = target.closest(".js-product-grid-item");

      if (gridItem?.dataset.currentVariantId) {
        id = parseInt(gridItem.dataset.currentVariantId, 10);
        quantity = 1;
      } else {
        let productId = target.getAttribute("data-product-id");
        let productElement = document.querySelector(`.js-product[data-product-id='${productId}']`);

        quantity = productElement?.querySelector(".js-quantity-field")?.value || 1;
        if (isNaN(quantity) || !quantity) quantity = 1;

        id = productElement
          ? parseInt(productElement.querySelector(`#product-select-${productId}`)?.value, 10)
          : null;
      }

      let featuredImage = target.getAttribute("data-featured-image") || null;

      // `sections` is bundled section rendering on the Cart API: the add response carries the
      // re-rendered drawer alongside it, so one request replaces what used to be three
      // (/cart/add.js, then /cart.js, then ?sections=drawer-cart). sections_url gives Shopify
      // the page context to render against.
      let formData = {
       'items': [{
          'id': parseInt(id, 10),
          'quantity': parseInt(quantity, 10)
        }],
        'sections': 'drawer-cart',
        'sections_url': window.location.pathname
      };

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      .then(async response => {
        if (!response.ok) {
          const errorData = await response.json();
          const error = new Error(errorData.description || 'Something went wrong');
          error.status = response.status;
          throw error;
        }
        return response.json();
      })
      .then(payload => {
        // Stash the bundled sections before narrowing to the added line item below.
        const sections = payload.sections;
        let response = payload.items.find(item => item.id === parseInt(id, 10));

        // Fires on a confirmed add, not on the click, so the funnel is not inflated by
        // failed adds. product_handle comes off the PDP element rather than a Mixpanel
        // super-property: super-properties persist across pages, so a grid add would
        // otherwise inherit whichever PDP was viewed last. A grid add has no .js-product and
        // simply carries no handle, which `source` already distinguishes.
        if (window.mixpanel && typeof window.mixpanel.track === 'function') {
          const pdp = gridItem ? null : document.querySelector(`.js-product[data-product-id='${target.getAttribute('data-product-id')}']`);
          const props = {
            product_id: response.product_id,
            variant_id: response.variant_id,
            quantity: response.quantity,
            source: gridItem ? 'grid' : 'pdp'
          };

          if (pdp) {
            props.product_handle = pdp.dataset.productHandle;
            // The v2 layout A/B arms. Both ends of the funnel have to carry them or the test
            // is only measurable at PDP Viewed, never at the conversion step it exists to
            // move. Read off the element for the same reason the handle is: a super-property
            // would persist across pages and mislabel a later add from a grid.
            props.pdp_layout_variant = pdp.dataset.layoutVariant;
            props.pdp_picker_variant = pdp.dataset.pickerVariant;
          }

          // "PDP Add to Cart", not "Product Added to Cart": the store's Shopify web pixel
          // already emits that name, so two sources under one name would double-count.
          window.mixpanel.track('PDP Add to Cart', props);
        }

        self.hideUpsellProductInCart(response.product_id);

        let variantId = response.variant_id;
        let cartItem = document.querySelector(`.js-cart-item[data-variant-id='${variantId}']:not(.hidden)`);

        if (cartItem) {
          let quantityField = cartItem.querySelector(".js-quantity-field-input");
          let quantityMinus = cartItem.querySelector(".js-quantity-minus");
          let quantityPlus = cartItem.querySelector(".js-quantity-plus");

          quantityField.value = response.quantity;
          quantityMinus.setAttribute("data-quantity", response.quantity - 1);
          quantityPlus.setAttribute("data-quantity", response.quantity + 1);
        } else {
          let quantityMinus = response.quantity - 1;
          let quantityPlus = response.quantity + 1;
          
          let image = featuredImage || response.image;

          let item = `
          <li class="cart-item d-flex js-cart-item" data-variant-id="${response.variant_id}" data-quantity="${response.quantity}">
            <div class="cart-item__image">
              <a href="${response.url}">
                <img src="${image}" width="668px" height="768px" alt="${response.product_title}" />
              </a>
            </div>

            <div class="cart-item__details d-flex">
              <div class="cart-item__top d-flex">
                <div class="cart-item__top-left">
                  <div class="cart-item__title">
                    <a href="${response.url}">${response.product_title}</a>
                  </div>

                  ${(response.options_with_values || [])
                    .filter(option => option.name !== 'Title')
                    .map(option => `<div class="cart-item__option">${option.value}</div>`)
                    .join('')}
                </div>

                <div class="cart-item__top-right">
                  <div class="cart-item__price">${self.linePriceMarkup(response)}</div>
                </div>
              </div>

              <div class="cart-product__quantity-and-delete d-flex align-items-center js-quantity-fields">
                <div class="cart-product__quantity-field">
                  <div class="quantity-field__minus">
                    <a href="#" class="js-change-quantity js-quantity-minus" data-variant-id="${response.variant_id}" data-quantity="${Math.max(0, response.quantity - 1)}">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.66667 5.66602H3.33333C3.14924 5.66602 3 5.81525 3 5.99935C3 6.18344 3.14924 6.33268 3.33333 6.33268H8.66667C8.85076 6.33268 9 6.18344 9 5.99935C9 5.81525 8.85076 5.66602 8.66667 5.66602Z" fill="black"/>
                      </svg>
                    </a>
                  </div>

                  <input type="text" value="${response.quantity}" class="quantity-field__input js-quantity-field-input" disabled="disabled" />

                  <div class="quantity-field__plus">
                    <a href="#" class="js-change-quantity js-quantity-plus" data-variant-id="${response.variant_id}" data-quantity="${response.quantity + 1}">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.0001 5.66602H6.33339V1.99935C6.33339 1.81525 6.18415 1.66602 6.00006 1.66602C5.81596 1.66602 5.66672 1.81525 5.66672 1.99935V5.66602H2.00006C1.81596 5.66602 1.66672 5.81525 1.66672 5.99935C1.66339 6.087 1.69817 6.1718 1.76208 6.23187C1.82599 6.29195 1.91278 6.32142 2.00006 6.31268H5.66672V9.99935C5.66672 10.1834 5.81596 10.3327 6.00006 10.3327C6.18415 10.3327 6.33339 10.1834 6.33339 9.99935V6.33268H10.0001C10.1842 6.33268 10.3334 6.18344 10.3334 5.99935C10.3334 5.81525 10.1842 5.66602 10.0001 5.66602Z" fill="black"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <div class="cart-item__delete">
                  <a href="#" class="js-change-quantity" data-variant-id="${response.variant_id}" data-quantity="0">Remove</a>
                </div>
              </div>
            </div>
          </li>`;

          document.querySelector(".js-cart-items").insertAdjacentHTML("beforeend", item);
          if (typeof initializeObservers === "function") {
            initializeObservers();
          }
        }

        if (typeof ProductGridLazyVariants !== "undefined") {
          ProductGridLazyVariants.closeMobileAddToCart();
        }
        document.querySelectorAll(".js-change-quantity").forEach(el => el.classList.remove("loading"));

        // `self`, not `this` — the enclosing click listener is a plain function, so `this`
        // in here is document.body. handleAddToCart already captures the element as `self`.
        //
        // The line item appended above went onto whatever this page happened to render, which
        // may have been stale, and the count and subtotal still need to come from the server.
        // The bundled markup settles all of it in one go; the fetch is only a fallback for a
        // store or API version that did not honour the `sections` parameter.
        if (sections && sections['drawer-cart']) {
          self.applyCartMarkup(sections['drawer-cart']);
        } else {
          self.syncCartFromServer();
        }

        // The drawer is already open — see openDrawer() at click time.
        document.querySelectorAll('.js-add-to-cart').forEach(button => {
          const originalText = button.getAttribute('data-original-text');
          button.textContent = originalText;
        });
	    })
      .catch(async (error) => {
        let message = 'Unexpected error';

        try {
          const text = await error.text();
          const json = JSON.parse(text);
          if (json && json.description) {
            message = json.description;
          }
        } catch (e) {
          message = error.message || 'Something went wrong';
        }

        document.querySelector('.js-cart-content')?.classList.remove('drawer-cart__content--adding');
        alert(message);
        window.location.reload();
      });
		});
  }

  hideUpsellProductInCart(productId) {
    if (!productId) return;

    const styleId = 'cart-products-hidden-css';
    let style = document.getElementById(styleId);

    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }

    const rule = `
      .cart-upsell .product-grid-item[data-product-id-2="${productId}"] {
        display: none !important;
      }
    `;

    if (!style.textContent.includes(`data-product-id-2="${productId}"`)) {
      style.textContent += rule;
    }
  }

}

customElements.define('drawer-cart', Cart);